import { Injectable, inject, signal, computed } from '@angular/core';
import { Subscription } from 'rxjs';
import { TenantDb } from '../data/tenant-collection';
import { ExtendedStockTransfer } from './stock-transfer.service';

/**
 * Transfer notification types
 */
export type TransferNotificationType =
  | 'new_incoming' // New transfer requested TO user's location
  | 'in_transit' // Transfer shipped TO user's location
  | 'overdue' // Transfer not received within expected time (48h+)
  | 'completed'; // Transfer completed

/**
 * Transfer notification model
 */
export interface TransferNotification {
  id: string;
  type: TransferNotificationType;
  transfer: ExtendedStockTransfer;
  read: boolean;
  createdAt: Date;
}

/**
 * Transfer Notification Service
 * Manages real-time notifications for transfers affecting a user's location
 */
@Injectable({
  providedIn: 'root',
})
export class TransferNotificationService {
  /**
   * The transfer documents themselves. Tenant scoping, the Timestamp<->Date codec, and the
   * real-time listener all live behind the seam — this service only describes the query.
   */
  private transfers = inject(TenantDb).collection<ExtendedStockTransfer>('stock_transfers');

  // Signals for reactive state
  private _notifications = signal<TransferNotification[]>([]);
  private _readNotificationIds = signal<Set<string>>(new Set());

  // Public computed signals
  notifications = computed(() => {
    const notifs = this._notifications();
    const readIds = this._readNotificationIds();
    return notifs.map(n => ({
      ...n,
      read: readIds.has(n.id),
    }));
  });

  unreadCount = computed(() => {
    const readIds = this._readNotificationIds();
    return this._notifications().filter(n => !readIds.has(n.id)).length;
  });

  // Live-read subscription (RxJS owns teardown; we hold it to detach on re-subscribe).
  private subscription: Subscription | null = null;
  private currentBusinessId: string | null = null;
  private currentLocationId: string | null = null;

  /**
   * Subscribe to transfers affecting a specific location
   * Will notify about:
   * - New transfers TO this location (incoming)
   * - Transfers shipped TO this location (in-transit)
   * - Overdue transfers (48h+ without receiving)
   */
  subscribeToLocationTransfers(businessId: string, locationId: string): void {
    // Skip if already subscribed to same location
    if (
      this.currentBusinessId === businessId &&
      this.currentLocationId === locationId &&
      this.subscription
    ) {
      return;
    }

    // Cleanup previous subscription
    this.unsubscribe();

    this.currentBusinessId = businessId;
    this.currentLocationId = locationId;

    // Live read of incoming transfers (pending or in-transit) TO this location, newest first.
    // Tenant scoping and the Timestamp<->Date codec are handled by the seam; this method only
    // maps the rows into notifications. RxJS owns listener teardown (see {@link unsubscribe}).
    this.subscription = this.transfers
      .observe({
        where: [
          ['toLocationId', '==', locationId],
          ['status', 'in', ['pending', 'in-transit']],
        ],
        orderBy: { field: 'createdAt', direction: 'desc' },
      })
      .subscribe(transfers => {
        const notifications: TransferNotification[] = [];
        const now = new Date();
        const overdueThreshold = 48 * 60 * 60 * 1000; // 48 hours in ms

        transfers.forEach(transfer => {
          // Determine notification type
          let type: TransferNotificationType;
          let notificationDate: Date;

          if (transfer.status === 'in-transit') {
            // Check if overdue (48h+ since shipped)
            const shippedAt = transfer.shippedAt;
            if (shippedAt && now.getTime() - shippedAt.getTime() > overdueThreshold) {
              type = 'overdue';
              notificationDate = shippedAt;
            } else {
              type = 'in_transit';
              notificationDate = shippedAt || transfer.createdAt || now;
            }
          } else {
            type = 'new_incoming';
            notificationDate = transfer.createdAt || now;
          }

          notifications.push({
            id: `${transfer.id}-${type}`,
            type,
            transfer,
            read: false, // Will be computed from readIds
            createdAt: notificationDate,
          });
        });

        // Sort by date (most recent first)
        notifications.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

        this._notifications.set(notifications);
      });
  }

  /**
   * Mark a notification as read
   */
  markAsRead(notificationId: string): void {
    this._readNotificationIds.update(ids => {
      const newIds = new Set(ids);
      newIds.add(notificationId);
      return newIds;
    });

    // Persist to localStorage
    this.persistReadState();
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    const allIds = this._notifications().map(n => n.id);
    this._readNotificationIds.set(new Set(allIds));

    // Persist to localStorage
    this.persistReadState();
  }

  /**
   * Clear read state for a notification (mark as unread)
   */
  markAsUnread(notificationId: string): void {
    this._readNotificationIds.update(ids => {
      const newIds = new Set(ids);
      newIds.delete(notificationId);
      return newIds;
    });

    this.persistReadState();
  }

  /**
   * Persist read notification IDs to localStorage
   */
  private persistReadState(): void {
    const key = this.getStorageKey();
    if (key) {
      const ids = Array.from(this._readNotificationIds());
      localStorage.setItem(key, JSON.stringify(ids));
    }
  }

  /**
   * Load read state from localStorage
   */
  private loadReadState(): void {
    const key = this.getStorageKey();
    if (key) {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const ids = JSON.parse(stored) as string[];
          this._readNotificationIds.set(new Set(ids));
        }
      } catch {
        // Ignore parse errors
      }
    }
  }

  /**
   * Get localStorage key for read state
   */
  private getStorageKey(): string | null {
    if (this.currentBusinessId && this.currentLocationId) {
      return `transfer_notifications_read_${this.currentBusinessId}_${this.currentLocationId}`;
    }
    return null;
  }

  /**
   * Cleanup subscription
   */
  unsubscribe(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    this.currentBusinessId = null;
    this.currentLocationId = null;
    this._notifications.set([]);
  }

  /**
   * Get notification icon based on type
   */
  getNotificationIcon(type: TransferNotificationType): string {
    switch (type) {
      case 'new_incoming':
        return 'inventory_2';
      case 'in_transit':
        return 'local_shipping';
      case 'overdue':
        return 'warning';
      case 'completed':
        return 'check_circle';
      default:
        return 'notifications';
    }
  }

  /**
   * Get notification title based on type
   */
  getNotificationTitle(type: TransferNotificationType): string {
    switch (type) {
      case 'new_incoming':
        return 'Nueva transferencia entrante';
      case 'in_transit':
        return 'Transferencia en camino';
      case 'overdue':
        return 'Transferencia vencida';
      case 'completed':
        return 'Transferencia completada';
      default:
        return 'Notificación';
    }
  }

}
